<template>
  <div v-if="showDebugger" class="auth-debugger">
    <div class="auth-debugger-content">
      <h3>Authentication Debugger</h3>
      <div class="debug-info">
        <div class="debug-section">
          <h4>Basic Info</h4>
          <p>
            <strong>Account:</strong>
            {{
              account ? account.username || "Unknown username" : "Not logged in"
            }}
          </p>
          <p><strong>Auth Type:</strong> {{ authStoreType || "Not set" }}</p>
          <p><strong>State:</strong> {{ authStore.state }}</p>
          <p><strong>Initialized:</strong> {{ initialized }}</p>
          <p><strong>Loading:</strong> {{ authStore.isLoading }}</p>
          <p><strong>Error:</strong> {{ error?.message || "None" }}</p>
        </div>

        <div class="debug-section">
          <h4>URLs</h4>
          <p><strong>Current:</strong> {{ currentUrl }}</p>
          <p><strong>Redirect URI:</strong> {{ redirectUri }}</p>
        </div>

        <p class="keyboard-hint">
          <em>Tip: Press Ctrl+Shift+Z to toggle debugger</em>
        </p>
      </div>
      <div class="debug-actions">
        <button class="btn-reload" @click="forceReload">Force Reload</button>
        <button
          v-if="isInRedirectLoop"
          class="btn-stop"
          @click="stopRedirectLoop"
        >
          Stop Redirect Loop
        </button>
        <button class="btn-export" @click="exportDiagnostics">
          Export Diagnostics
        </button>
        <button class="btn-hide" @click="hideDebugger">Hide</button>
      </div>
    </div>
  </div>
  <div v-else-if="hasAuthIssue || forceShow" class="debug-trigger">
    <button class="btn-show" @click="showDebugger = true">🔧 Auth Debug</button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { authStore } from "~/stores/authStore";
import { showError } from "~/utils/handleAPIError";
import { getEnvConfig } from "~/services/appConfig";

const showDebugger = ref(false);
const forceShow = ref(false); // manually set to true to force show the debugger

const account = computed(() => authStore.account);
const authStoreType = computed(() => authStore.authStoreType);
const initialized = computed(() => authStore.initialized);
const error = computed(() => authStore.error);
const currentUrl = computed(() => window.location.href);
const redirectUri = computed(() => getEnvConfig().entraRedirect);

const isInRedirectLoop = computed(
  () =>
    currentUrl.value.includes("login.microsoftonline.com") ||
    currentUrl.value.includes("reprocess"),
);

const hasAuthIssue = computed(
  () =>
    error.value ||
    isInRedirectLoop.value ||
    (!initialized.value && window.location.pathname !== "/") ||
    (initialized.value && !account.value && window.location.pathname !== "/"),
);

const exportDiagnostics = () => {
  try {
    const data = {
      timestamp: new Date().toISOString(),
      url: currentUrl.value,
      userAgent: navigator.userAgent,
      account: account.value,
      authStoreType: authStoreType.value,
      state: authStore.state,
      initialized: initialized.value,
      loading: authStore.isLoading,
      error: error.value?.message || "None",
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auth-diagnostics-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    showError("Failed to export diagnostics. Check console for details.", err);
  }
};

const forceReload = () => {
  window.location.reload();
};

const stopRedirectLoop = () => {
  console.log("🛑 Stopping redirect loop...");

  // Clear all auth-related storage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith("msal.") ||
        key.includes("auth") ||
        key.includes("token") ||
        key.includes("account"))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Navigate back to the redirect URI to break the loop
  console.log("🔄 Redirecting to:", redirectUri.value);
  window.location.replace(redirectUri.value);
};

const hideDebugger = () => {
  showDebugger.value = false;
};

// Keyboard shortcut to toggle debugger (Ctrl+Shift+Z)
const handleKeydown = (event: KeyboardEvent) => {
  if (event.ctrlKey && event.shiftKey && event.key === "Z") {
    event.preventDefault();
    forceShow.value = !forceShow.value;
    if (forceShow.value) {
      showDebugger.value = true;
    }
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.auth-debugger {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 2px solid #e74c3c;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.debug-trigger {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.auth-debugger-content h3 {
  margin: 0 0 12px 0;
  color: #e74c3c;
  font-size: 16px;
}

.debug-info {
  margin-bottom: 12px;
  font-size: 12px;
}

.debug-info p {
  margin: 4px 0;
  word-break: break-all;
}

.debug-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.debug-actions button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.btn-reload {
  background: #3498db;
  color: white;
}

.btn-hide {
  background: #95a5a6;
  color: white;
}

.btn-stop {
  background: #e67e22;
  color: white;
}

.btn-export {
  background: #27ae60;
  color: white;
}

.debug-section {
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.debug-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #2c3e50;
}

.keyboard-hint {
  font-size: 10px;
  color: #666;
  margin-top: 8px;
}

.btn-show {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.debug-actions button:hover,
.btn-show:hover {
  opacity: 0.8;
}
</style>
